import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, ArrowRight, Loader2, FolderKanban, Layers, Table2,
  ShieldCheck, Search, ChevronLeft, ChevronRight, Check,
} from 'lucide-react';
import { projectsApi, onboardingApi } from '../../services/api';

interface Props {
  connection: Record<string, any>;
  selectedProjectId: number | null;
  selectedGroupId: number | null;
  selectedProjectName: string;
  selectedGroupName: string;
  schema: string;
  table: string;
  onSchemaChange: (val: string) => void;
  onTableChange: (val: string) => void;
  onSelect: (project: any, group: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const PROJECTS_PER_PAGE = 5;

const ProjectSelector: React.FC<Props> = ({
  connection,
  selectedProjectId,
  selectedGroupId,
  selectedProjectName,
  selectedGroupName,
  schema,
  table,
  onSchemaChange,
  onTableChange,
  onSelect,
  onNext,
  onBack,
}) => {
  const [projects, setProjects] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [error, setError] = useState('');

  // Pagination
  const [projectPage, setProjectPage] = useState(1);
  const [projectTotalPages, setProjectTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Schema & Table selection (same as Source Profiling)
  const [schemas, setSchemas] = useState<string[]>([]);
  const [tables, setTables] = useState<any[]>([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const schemasFetched = useRef(false);

  // ─── Load projects ───
  const loadProjects = useCallback(async (page: number, search?: string) => {
    setLoadingProjects(true);
    setError('');
    try {
      const params: Record<string, any> = { page, items_per_page: PROJECTS_PER_PAGE };
      if (search?.trim()) params.search = search.trim();
      const resp = await projectsApi.list(params);
      const payload = resp.data.payload;
      const list = Array.isArray(payload) ? payload : payload?.projects || payload?.projectlist || [];
      setProjects(list);
      const pagination = payload?.pagination;
      if (pagination) {
        setProjectTotalPages(pagination.pages || Math.ceil(pagination.count / PROJECTS_PER_PAGE) || 1);
      } else {
        setProjectTotalPages(1);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => { loadProjects(1); }, [loadProjects]);

  // ─── Load schemas (once) ───
  useEffect(() => {
    if (schemasFetched.current || !connection.connector_type) return;
    schemasFetched.current = true;
    setLoadingSchemas(true);
    onboardingApi.listSchemas(connection as any)
      .then((resp) => setSchemas(resp.data.payload || []))
      .catch(() => setSchemas([]))
      .finally(() => setLoadingSchemas(false));
  }, [connection]);

  // ─── Handlers ───
  const handlePageChange = (newPage: number) => {
    setProjectPage(newPage);
    loadProjects(newPage, searchQuery);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    if (searchTimer) clearTimeout(searchTimer);
    const timer = setTimeout(() => {
      setProjectPage(1);
      loadProjects(1, val);
    }, 400);
    setSearchTimer(timer);
  };

  const handleProjectSelect = async (project: any) => {
    const projectId = project.id || project.project_id;
    const projectName = project.project_name || project.name || '';
    onSelect({ id: projectId, project_name: projectName }, null);
    setLoadingGroups(true);
    setGroups([]);
    setError('');
    try {
      const resp = await projectsApi.groups(projectId);
      const payload = resp.data.payload;
      const list = Array.isArray(payload) ? payload : payload?.groups || payload?.grouplist || [];
      setGroups(list);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleGroupSelect = (group: any) => {
    const groupId = group.id || group.group_id;
    const groupName = group.group_name || group.name || '';
    onSelect(
      { id: selectedProjectId, project_name: selectedProjectName },
      { id: groupId, group_name: groupName }
    );
  };

  const handleSchemaSelect = async (s: string) => {
    onSchemaChange(s);
    onTableChange('');
    setLoadingTables(true);
    setTables([]);
    try {
      const resp = await onboardingApi.listTables({ ...connection, schema: s });
      setTables(resp.data.payload || []);
    } catch {
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const handleTableSelect = (t: string) => {
    onTableChange(t);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-cargo-text mb-4">Select Project, Group & Table</h2>

      <div className="space-y-4">
        {/* ═══ Projects ═══ */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3 flex items-center gap-2">
            <FolderKanban className="w-4 h-4" /> Projects
          </h3>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
            <input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search projects by name..."
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-cargo-panel border border-cargo-border
                         text-cargo-text placeholder-cargo-muted/50 text-sm
                         focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
            />
          </div>

          {loadingProjects ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-cargo-accent" />
              <span className="ml-2 text-cargo-muted text-sm">Loading projects...</span>
            </div>
          ) : projects.length === 0 ? (
            <p className="text-cargo-muted text-sm py-4 text-center">
              {searchQuery ? 'No projects match your search.' : 'No projects found.'}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {projects.map((p) => {
                  const pid = p.id || p.project_id;
                  const pname = p.project_name || p.name || `Project ${pid}`;
                  const isSelected = selectedProjectId === pid;
                  return (
                    <button
                      key={pid}
                      onClick={() => handleProjectSelect(p)}
                      className={`text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                        isSelected
                          ? 'bg-cargo-accent/15 border-cargo-accent text-cargo-text'
                          : 'bg-cargo-panel border-cargo-border text-cargo-text hover:border-cargo-accent/50'
                      }`}
                    >
                      <p className="font-medium">{pname}</p>
                      <p className="text-xs text-cargo-muted mt-0.5">ID: {pid}</p>
                    </button>
                  );
                })}
              </div>
              {projectTotalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-3">
                  <button
                    onClick={() => handlePageChange(Math.max(1, projectPage - 1))}
                    disabled={projectPage <= 1}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-cargo-panel rounded text-cargo-muted
                               hover:text-cargo-text disabled:opacity-30"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Prev
                  </button>
                  <span className="text-xs text-cargo-muted">Page {projectPage} of {projectTotalPages}</span>
                  <button
                    onClick={() => handlePageChange(Math.min(projectTotalPages, projectPage + 1))}
                    disabled={projectPage >= projectTotalPages}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-cargo-panel rounded text-cargo-muted
                               hover:text-cargo-text disabled:opacity-30"
                  >
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ═══ Groups ═══ */}
        {selectedProjectId && (
          <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
            <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Groups in {selectedProjectName}
            </h3>
            {loadingGroups ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-cargo-accent" />
                <span className="ml-2 text-cargo-muted text-sm">Loading groups...</span>
              </div>
            ) : groups.length === 0 ? (
              <p className="text-cargo-muted text-sm py-4 text-center">No groups found.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                {groups.map((g) => {
                  const gid = g.id || g.group_id;
                  const gname = g.group_name || g.name || `Group ${gid}`;
                  const rulesCount = g.applied_rules?.length || 0;
                  const isSelected = selectedGroupId === gid;
                  return (
                    <button
                      key={gid}
                      onClick={() => handleGroupSelect(g)}
                      className={`text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                        isSelected
                          ? 'bg-cargo-accent/15 border-cargo-accent text-cargo-text'
                          : 'bg-cargo-panel border-cargo-border text-cargo-text hover:border-cargo-accent/50'
                      }`}
                    >
                      <p className="font-medium">{gname}</p>
                      <div className="flex gap-4 text-xs text-cargo-muted mt-0.5">
                        <span>ID: {gid}</span>
                        {rulesCount > 0 && <span>{rulesCount} rules</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ Schema & Table (same as Source Profiling) ═══ */}
        {selectedGroupId && (
          <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
            <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3 flex items-center gap-2">
              <Table2 className="w-4 h-4" /> Select Table to Validate
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Schema list */}
              <div>
                <p className="text-xs font-semibold text-cargo-muted uppercase tracking-wider mb-2">Schemas</p>
                {loadingSchemas ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-cargo-accent" />
                  </div>
                ) : schemas.length === 0 ? (
                  <p className="text-cargo-muted text-xs py-4 text-center">No schemas found</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {schemas.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSchemaSelect(s)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          schema === s
                            ? 'bg-cargo-accent/15 text-cargo-accent font-medium'
                            : 'text-cargo-text hover:bg-cargo-hover'
                        }`}
                      >
                        <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5" />
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Table list */}
              <div className="md:col-span-2">
                <p className="text-xs font-semibold text-cargo-muted uppercase tracking-wider mb-2">
                  Tables {table && <span className="text-cargo-accent normal-case">(1 selected)</span>}
                </p>
                {!schema ? (
                  <p className="text-cargo-muted text-xs py-6 text-center">Select a schema to see tables</p>
                ) : loadingTables ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-cargo-accent" />
                  </div>
                ) : tables.length === 0 ? (
                  <p className="text-cargo-muted text-xs py-6 text-center">No tables found</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {tables.map((t: any) => {
                      const tname = t.table_name || t;
                      const isSelected = table === tname;
                      return (
                        <button
                          key={tname}
                          onClick={() => handleTableSelect(tname)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                            isSelected
                              ? 'bg-cargo-accent/15 text-cargo-text'
                              : 'text-cargo-text hover:bg-cargo-hover'
                          }`}
                        >
                          <span className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'bg-cargo-accent border-cargo-accent'
                              : 'border-cargo-border'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <Table2 className="w-4 h-4 text-cargo-muted flex-shrink-0" />
                          <span>{tname}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4 text-cargo-error text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!selectedProjectId || !selectedGroupId || !schema || !table}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-6 rounded-lg transition-colors disabled:opacity-30"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ProjectSelector;
